import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';

import { ROUTES } from '@/app/router/routes';
import { AlertCircleIcon, ArrowRightIcon, MailIcon } from '@/components/icons';
import { Logo } from '@/components/brand/Logo';
import { Checkbox } from '@/components/ui/Checkbox';
import cubes from '@/assets/images/cubes.png';
import './LoginPage.css';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';

import { PasswordField } from '../components/PasswordField';
import { useAuth } from '../context/auth-context';
import { useLogin } from '../hooks/useLogin';
import { loginSchema, type LoginFormValues } from '../schemas/login.schema';

/**
 * Connexion à l'espace consultant.
 *
 * ── Une page sobre, sans vitrine ─────────────────────────────────────────
 * L'outil interne accueille des recruteurs plusieurs fois par jour et peut
 * s'offrir une mise en scène. Un consultant s'y connecte une fois par mois,
 * souvent depuis un téléphone, pour une tâche précise. La page va donc droit
 * au formulaire.
 *
 * Aucun lien d'inscription : un accès s'ouvre depuis l'outil interne au
 * démarrage d'une mission, jamais à la demande.
 */
export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, setSession } = useAuth();

  const login = useLogin({
    onSuccess: (data) => {
      setSession(data.accessToken, data.user);

      /* `state.from` mémorise la destination voulue : on y renvoie plutôt que
         de déposer systématiquement sur la liste des CRA. */
      const from = (location.state as { from?: string } | null)?.from;
      void navigate(from ?? ROUTES.timesheets, { replace: true });
    },
  });

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', rememberMe: true },
  });

  /* Déjà connecté : on ne montre pas un formulaire de connexion à quelqu'un
     qui l'a déjà franchi — le bouton « retour » y revient souvent. */
  if (status === 'authenticated') return <Navigate to={ROUTES.timesheets} replace />;

  return (
    <main className="portal-login">
      <div className="portal-login__form">
        <Logo />
        <div className="portal-login__intro">
          <span>Espace consultant</span>
          <h1>Heureux de vous retrouver.</h1>
          <p>Vos missions, votre activité et vos congés, au même endroit.</p>
        </div>

        <form
          onSubmit={(event) => {
            void form.handleSubmit((values) => {
              login.mutate({
                email: values.email,
                password: values.password,
                rememberMe: values.rememberMe,
              });
            })(event);
          }}
          className="space-y-5"
        >
          {(location.state as { passwordChanged?: boolean } | null)?.passwordChanged === true && (
            <p
              role="status"
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800"
            >
              Votre mot de passe a été modifié. Reconnectez-vous avec votre nouveau mot de passe.
            </p>
          )}
          <TextField
            label="Adresse e-mail"
            type="email"
            icon={<MailIcon />}
            autoComplete="username"
            autoFocus
            error={form.formState.errors.email?.message}
            {...form.register('email')}
          />

          <PasswordField
            label="Mot de passe"
            autoComplete="current-password"
            error={form.formState.errors.password?.message}
            {...form.register('password')}
          />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Checkbox label="Se souvenir de moi" {...form.register('rememberMe')} />
            <Link
              to={ROUTES.passwordReset}
              className="text-accent-700 rounded text-xs hover:underline focus-visible:outline-2"
            >
              Mot de passe oublié ?
            </Link>
          </div>

          {login.isError ? (
            <p role="alert" className="portal-notice">
              <AlertCircleIcon className="mt-0.5 shrink-0 text-base" />
              {login.error.message}
            </p>
          ) : null}

          <Button
            type="submit"
            className="w-full"
            loading={login.isPending}
            loadingLabel="Connexion…"
          >
            Se connecter
            <ArrowRightIcon />
          </Button>
        </form>

        <p className="text-ink-500 mt-7 text-xs leading-6">
          Votre accès vous a été ouvert par ubeefy au démarrage de votre mission.
        </p>
      </div>
      <aside className="portal-login__visual" aria-label="Votre espace ubeefy">
        <img src={cubes} alt="" />
        <div>
          <span>Votre quotidien, simplifié.</span>
          <h2>
            Concentrez-vous
            <br />
            sur votre mission.
          </h2>
          <p>Un espace pour déclarer votre activité et garder le lien avec ubeefy.</p>
        </div>
      </aside>
    </main>
  );
};
